import { SHEET_ID } from '../config.js';

/**
 * Descubre las hojas (pestañas) del Google Sheet conectado.
 *
 * Estrategia: Google sirve un endpoint público `/htmlview` para sheets compartidos
 * con "Cualquiera con el enlace". Ese HTML embebe un JS con la lista de pestañas
 * en la forma `items.push({name: "X", ..., gid: "Y"})`. Lo parseamos.
 *
 * CORS: Google reflecta cualquier `Origin` en la respuesta (verificado para
 * localhost y dominios de producción), así que se puede llamar directo desde
 * el navegador sin proxy.
 *
 * Fallbacks: si el fetch falla (red, sheet privado, formato cambió), devuelve
 * lista vacía y el dashboard sigue funcionando con "Hoja por defecto".
 *
 * @returns {Promise<Array<{name: string, gid: string}>>}
 */
export async function fetchSheetsList() {
  const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/htmlview`;
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  const html = await res.text();

  const items = [];
  const re = /items\.push\(\{name:\s*"((?:[^"\\]|\\.)*)"[^}]*gid:\s*"(\d+)"/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    items.push({
      name: unescapeJsString(m[1]),
      gid: m[2],
    });
  }
  // El primer push de Google suele ser la hoja en la URL actual; el orden total
  // refleja el orden visual de pestañas pero invertido a veces. Devolvemos
  // ordenado por gid ascendente (gid=0 es la primera, etc.) para estabilidad.
  items.sort((a, b) => Number(a.gid) - Number(b.gid));
  return items;
}

function unescapeJsString(s) {
  // El HTML viene con escapes JS estándar: \", \\, \/, \n, é, etc.
  return s
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/\\(["\\/])/g, '$1');
}

/**
 * Heurística para distinguir hojas de COSTOS (no son ventas, no se combinan
 * en el dropdown ni en el modo "Todas las hojas"). Match flexible para
 * tolerar renombres como "Costos", "Gastos", "COSTOS / GASTOS", etc.
 */
export function isCostsSheet(name) {
  return /cost|gast/i.test(String(name || ''));
}

/**
 * Separa la lista cruda en (a) hojas de venta y (b) la primera hoja de costos.
 * @returns {{ salesSheets: Array<{name,gid}>, costsSheet: {name,gid}|null }}
 */
export function splitSheets(list) {
  const sales = [];
  let costs = null;
  for (const s of list) {
    if (!costs && isCostsSheet(s.name)) costs = s;
    else sales.push(s);
  }
  return { salesSheets: sales, costsSheet: costs };
}
